import Complete from "./Complete";
import { Omit } from "@material-ui/core";
import { AutocompleteChangeReason, AutocompleteProps } from "@material-ui/lab/Autocomplete";
import { useField, UseFieldProps } from "formik";
import { memo, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import NewCompleteInput from "./NewCompleteInput";
import { PaperComponent, paperStyle } from "./OutlinedPopper";
import { isEmptyValue } from "../utils/is-empty-value";

interface Props {
	id?: string;
	title: string;
	optionLabel?: string;
	required?: boolean;
	property?: string;
	selectPropertyValue?: boolean;
	onClear?: () => void;
	isChildInFormik?: boolean;
}

const defaultProperty = "id";
export const NON_OPTION_VALUE = "none-option";

function selectedValue<T>(actualProperty: keyof T, v: T | T[]) {
	if (isEmptyValue(v)) {
		return null;
	}

	return Array.isArray(v) ? v.map((item) => item?.[actualProperty]) : v?.[actualProperty];
}

function LAComplete<
	T,
	Multiple extends boolean | undefined = undefined,
	DisableClearable extends boolean | undefined = undefined,
	FreeSolo extends boolean | undefined = undefined
>(props: Props & Omit<AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>, "renderInput"> & UseFieldProps<any>) {
	const { t } = useTranslation();
	const [{ value, ...fieldProps }, input, helpers] = useField(props);
	const { property, selectPropertyValue, onClear, ...forwardedProps } = props;
	const actualProperty = (property || defaultProperty) as keyof T;
	const isError = input.touched && !!input.error;
	const [isCleared, setIsCleared] = useState(false);
	const { paper } = paperStyle();

	const matchValues = selectPropertyValue
		? (option: T, propertyValue: T | T[keyof T]) => selectedValue(actualProperty, option) === propertyValue
		: (option: T, propertyValue: T | T[keyof T]) =>
				selectedValue(actualProperty, option) === selectedValue(actualProperty, propertyValue as T);

	const originalValue = useMemo(() => {
		if (value === "") {
			return null;
		}

		if (isEmptyValue(value) || !forwardedProps.options) {
			return value;
		}

		return forwardedProps.multiple
			? (value as T[]).map((v) => forwardedProps.options.find((o) => matchValues(o, v))).filter(Boolean)
			: forwardedProps.options.find((o) => matchValues(o, value)) || null;
	}, [value, forwardedProps.options, forwardedProps.multiple, selectPropertyValue, selectedValue]);

	useEffect(() => {
		if (props.options.length === 1 && !!props.name && !isCleared) {
			const valueToSet = selectPropertyValue ? selectedValue(actualProperty, props.options[0]) : props.options[0];
			if (!isEmptyValue(valueToSet) && !matchValues(props.options[0], value) && (valueToSet as any)?.value !== NON_OPTION_VALUE) {
				helpers.setValue(valueToSet);
			}
		}

		if (!isEmptyValue(value) && isEmptyValue(originalValue)) {
			helpers.setValue("");
		}
	}, [props.options, originalValue, helpers, props.name, isCleared]);

	const optionKeys = props.options.map((o) => selectedValue(actualProperty, o)).join("|");
	useEffect(() => {
		setIsCleared(false);
	}, [optionKeys, setIsCleared]);

	function handleSelectValue(value: T, reason: AutocompleteChangeReason) {
		helpers.setValue(selectPropertyValue ? selectedValue(actualProperty, value as any) : value);
		setIsCleared(reason === "clear");
		reason === "clear" && onClear && onClear();
	}

	return (
		<Complete
			id={props.id || fieldProps.name}
			{...fieldProps}
			{...forwardedProps}
			value={props.isChildInFormik ? value : originalValue}
			onChange={(_, v, reason) => handleSelectValue(v as any, reason)}
			optionLabel={props.optionLabel || "label"}
			noOptionsText={t("no_values_found")}
			PaperComponent={!props.PaperComponent ? PaperComponent : props.PaperComponent}
			classes={{ paper }}
			clearOnBlur={false}
			autoSelect
			renderInput={(params) => {
				return (
					<NewCompleteInput
						disabled={props.disabled}
						error={isError ? t(input.error || " ") : undefined}
						params={{
							...params,
						}}
						label={props.title}
						required={props.required}
					/>
				);
			}}
		/>
	);
}

export default memo(LAComplete);
